'use client';

import React, { useCallback, useEffect } from 'react';
import { env } from 'env';
import type { AnalyticsContext as WhoopAnalyticsContext } from 'whoop-analytics';
import analytics from 'whoop-analytics';
import type { Language } from '@whoop/i18n';
import { useUserStore } from '@/stores/StoreProvider';
import useLocale from '@/hooks/useLocale';
import { AnalyticsContext } from '@/hooks/analytics';

type ShopEventProperties = WhoopAnalyticsContext & {
  is_logged_in: boolean;
  is_employee?: boolean;
  language: Language;
  country: string;
  pathname: string;
  previous_pathname: string;
  user_id: number;
  on_compatible_page: string;
  page_compatibility: string;
  user_version: string;
  flow_type: string;
};

const paths: string[] = ['Initial'];

export default function AnalyticsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { user, isLoggedIn, strap } = useUserStore((state) => ({
    user: state.user,
    isLoggedIn: state.isLoggedIn,
    strap: state.strap,
  }));
  const { language, country } = useLocale();

  useEffect(() => {
    try {
      analytics.initialize({
        amplitude: {
          key: env.NEXT_PUBLIC_AMPLITUDE_API_KEY,
          options: {
            defaultTracking: {
              attribution: true,
              pageViews: {
                trackOn: 'attribution',
              },
              sessions: false,
              fileDownload: false,
              formInteraction: false,
            },
            cookieOptions: {
              domain: '.whoop.com',
            },
            includeReferrer: true,
            includeUtm: true,
          },
        },
        segment: {
          key: env.NEXT_PUBLIC_SEGMENT_API_KEY,
        },
        // TODO: Add datadog/GTM?
      });
    } catch (error) {
      // TODO: Add Sentry error tracking
      console.error('Failed to initialize analytics', error);
    }
  }, []);

  const trackPageView = useCallback(
    (pageName: string) => {
      analytics.trackAmplitudeEvent('Page View', {
        page_location: window.location.href.split('?')[0],
        page_title: pageName,
        language,
        country,
      });
    },
    [language, country],
  );

  const trackAmplitudeEvent = useCallback(
    (
      eventName: string,
      properties: WhoopAnalyticsContext,
      pathname?: string,
    ) => {
      if (pathname && pathname !== paths[paths.length - 1]) {
        paths.push(pathname);
      }
      const previousPage =
        pathname === paths[paths.length - 1]
          ? paths[paths.length - 2]
          : paths[paths.length - 1];

      const eventProperties: ShopEventProperties = {
        is_logged_in: isLoggedIn,
        user_id: user?.id ?? NaN,
        // TODO: Add is_employee HOW? like we can get user.memerbership.isPro or something
        language,
        country,
        pathname: pathname ?? 'unknown',
        previous_pathname: previousPage,
        page_compatibility: [].join(','), // TODO: Add page compatibility will this need to be a hook?
        user_version: strap ?? 'not logged in',
        on_compatible_page: 'NA', // TODO: Add on compatible page
        flow_type: 'shop',
      };
      analytics.trackAmplitudeEvent(eventName, {
        ...eventProperties,
        ...properties,
      });
    },
    [country, isLoggedIn, language, strap, user],
  );

  const analyticsFunctions = {
    trackPageView,
    trackAmplitudeEvent,
  };

  return (
    <AnalyticsContext.Provider value={analyticsFunctions}>
      {children}
    </AnalyticsContext.Provider>
  );
}
